import { getSupabaseClient, supabase } from '../lib/supabase';
import { getCustomerId } from '../lib/config';
import { ASNDocument, InvoiceDocument, Document, DocumentFilters, DocumentSort, DocumentStatus } from '../types/document';
import { ASNFormData } from '../types/asn';
import { InvoiceFormData } from '../types/invoice';

// ==========================================
// 1. GENERIC DOCUMENT FUNCTIONS
// ==========================================

export async function fetchDocumentStats(document_type: string): Promise<{ total: number; draft: number; valid: number; sent: number }> {
  const customerId = getCustomerId();
  let query = getSupabaseClient()
    .from('documents')
    .select('status', { count: 'exact' })
    .eq('document_type', document_type);

  if (customerId) {
    query = query.eq('user_id', customerId);
  }

  try {
    const { data, error } = await query;
    if (error) throw error;

    const stats = { total: 0, draft: 0, valid: 0, sent: 0 };
    if (data) {
      stats.total = data.length;
      data.forEach((doc) => {
        if (doc.status === 'draft') stats.draft++;
        else if (doc.status === 'valid') stats.valid++;
        else if (doc.status === 'sent') stats.sent++;
      });
    }
    return stats;
  } catch (error) {
    console.error('Error fetching document stats:', error);
    return { total: 0, draft: 0, valid: 0, sent: 0 };
  }
}

export async function fetchDocuments(
  document_type: string,
  filters?: DocumentFilters,
  sort?: DocumentSort
): Promise<{ data: Document[]; count: number }> {
  let query = getSupabaseClient()
    .from('documents')
    // WDEV-41 hot fix: MyRest does not yet support Supabase's `!left` join
    // modifier and was emitting it literally into SQL, breaking the composer
    // document list. Drop the embed until WDEV-11 lands the proper MyRest fix.
    .select('*', { count: 'exact' })
    .eq('document_type', document_type);

  // Filter by user_id from JWT — only show documents belonging to the current user
  const customerId = getCustomerId();
  if (customerId) {
    query = query.eq('user_id', customerId);
  }

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  if (filters?.searchQuery) {
    if (document_type === 'asn') {
      query = query.ilike('shipment_id', `%${filters.searchQuery}%`);
    } else if (document_type === 'invoice') {
      query = query.ilike('invoice_number', `%${filters.searchQuery}%`);
    } else {
      query = query.or(`shipment_id.ilike.%${filters.searchQuery}%,invoice_number.ilike.%${filters.searchQuery}%`);
    }
  }

  if (filters?.dateFrom) {
    query = query.gte('created_at', filters.dateFrom);
  }

  if (filters?.dateTo) {
    query = query.lte('created_at', filters.dateTo);
  }

  const sortConfig = sort || { field: 'created_at', direction: 'desc' };
  query = query.order(sortConfig.field as string, { ascending: sortConfig.direction === 'asc' });

  // Pagination
  if (filters?.page && filters?.pageSize) {
    const from = (filters.page - 1) * filters.pageSize;
    const to = from + filters.pageSize - 1;
    query = query.range(from, to);
  }

  try {
    const { data, error, count } = await query;

    if (error) {
      throw error;
    }

    // Map trading_partners.name to trading_partner_name for compatibility
    const processedData = (data || []).map((doc: any) => ({
      ...doc,
      trading_partner_name: doc.trading_partners?.name || undefined,
    }));

    return { data: processedData, count: count || 0 };
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
}

export async function deleteDocument(id: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('documents')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('deleteDocument failed:', error);
    throw new Error(`Failed to delete document ${id}: ${error.message || JSON.stringify(error)}`);
  }
}

export async function bulkDeleteDocuments(ids: string[]): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('documents')
    .delete()
    .in('id', ids);

  if (error) {
    console.error('bulkDeleteDocuments failed:', error);
    throw new Error(`Failed to bulk delete: ${error.message || JSON.stringify(error)}`);
  }
}


// ==========================================
// 3. ASN DOCUMENT FUNCTIONS
// ==========================================

export async function fetchASNDocumentById(id: string): Promise<ASNDocument | null> {
  try {
    const { data, error } = await getSupabaseClient()
      .from('documents')
      .select('*')
      .eq('id', id)
      .eq('document_type', 'asn')
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (data) {
      return data as ASNDocument;
    }
  } catch (error) {
    console.error('Error fetching document:', error);
    throw error;
  }
  return null;
}

export async function createASNDocument(
  formData: ASNFormData,
  userId: string,
  templateId: string,
  tradingPartnerId: string
): Promise<ASNDocument> {
  const totalPieces = formData.orders.reduce((sum, order) => {
    return sum + order.details.reduce((detailSum, detail) => {
      return detailSum + (parseInt(detail.unitsShipped) || 0);
    }, 0);
  }, 0);

  const totalWeight = parseFloat(formData.grossWeight) || 0;

  const document = {
    shipment_id: formData.shipmentIdentification,
    template_id: templateId,
    trading_partner_id: tradingPartnerId,
    document_type: 'asn',
    status: formData.status || 'draft',
    total_pieces: totalPieces,
    total_weight: totalWeight,
    data: formData,
    user_id: userId,
    shipment_date: formData.date
  };

  // Insert — MyRest may not return data on insert, so we insert then fetch
  const { error: insertError } = await supabase
    .from('documents')
    .insert(document);

  if (insertError) {
    console.error('createASNDocument insert failed:', insertError);
    throw new Error(`Failed to create ASN document: ${insertError.message || JSON.stringify(insertError)}`);
  }

  // Fetch the just-created document by shipment_id + user_id + document_type (unique enough)
  const { data: rows, error: fetchError } = await supabase
    .from('documents')
    .select('*')
    .eq('document_type', 'asn')
    .eq('shipment_id', document.shipment_id)
    .eq('user_id', document.user_id)
    .order('created_at', { ascending: false })
    .limit(1);

  if (fetchError || !rows || rows.length === 0) {
    console.error('createASNDocument fetch-after-insert failed:', fetchError);
    throw new Error('ASN document was inserted but could not be retrieved. Check database.');
  }

  const savedDoc = rows[0] as ASNDocument;
  return savedDoc;
}

export async function updateASNDocument(
  id: string,
  formData: ASNFormData,
  status: DocumentStatus
): Promise<ASNDocument> {
  const totalPieces = formData.orders.reduce((sum, order) => {
    return sum + order.details.reduce((detailSum, detail) => {
      return detailSum + (parseInt(detail.unitsShipped) || 0);
    }, 0);
  }, 0);

  const totalWeight = parseFloat(formData.grossWeight) || 0;

  const updates: Partial<ASNDocument> = {
    shipment_id: formData.shipmentIdentification,
    total_pieces: totalPieces,
    total_weight: totalWeight,
    data: formData,
    shipment_date: formData.date
  };

  if (status) {
    updates.status = status;
  }

  const { data, error } = await supabase
    .from('documents')
    .update(updates)
    .eq('id', id)
    .eq('document_type', 'asn')
    .select()
    .single();

  if (error) {
    console.error('updateASNDocument failed:', error);
    throw new Error(`Failed to update ASN document ${id}: ${error.message || JSON.stringify(error)}`);
  }

  const savedDoc = data as ASNDocument;
  return savedDoc;
}

export async function deleteASNDocument(id: string): Promise<void> {
  return deleteDocument(id);
}

export async function duplicateASNDocument(id: string, userId: string): Promise<ASNDocument> {
  const original = await fetchASNDocumentById(id);
  if (!original) {
    throw new Error('Document not found');
  }

  const newData = {
    ...original.data,
    shipmentIdentification: `${original.data.shipmentIdentification}-COPY`,
  };

  return createASNDocument(newData, userId, original.template_id, original.trading_partner_id);
}

export async function bulkUpdateASNStatus(ids: string[], status: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('documents')
    .update({ status })
    .in('id', ids);

  if (error) {
    console.error('bulkUpdateASNStatus failed:', error);
    throw new Error(`Failed to bulk update status: ${error.message || JSON.stringify(error)}`);
  }
}

export async function bulkDeleteASN(ids: string[]): Promise<void> {
  return bulkDeleteDocuments(ids);
}


// ==========================================
// 4. INVOICE DOCUMENT FUNCTIONS
// ==========================================

export async function fetchInvoiceDocuments(): Promise<InvoiceDocument[]> {
  try {
    const { data, error } = await getSupabaseClient()
      .from('documents')
      .select('*')
      .eq('document_type', 'invoice')
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    const combined = data;
    return combined;
  } catch (error) {
    console.error('Error fetching invoice documents:', error);
    throw error;
  }
  return [];
}

export async function fetchInvoiceDocumentById(id: string): Promise<InvoiceDocument | null> {
  try {
    const { data, error } = await getSupabaseClient()
      .from('documents')
      .select('*')
      .eq('id', id)
      .eq('document_type', 'invoice')
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (data) {
      return data as InvoiceDocument;
    }
  } catch (error) {
    console.error('Error fetching invoice document:', error);
    throw error;
  }
  return null;
}

export async function createInvoiceDocument(
  invoiceData: InvoiceFormData,
  userId: string,
  templateId: string,
  tradingPartnerId: string
): Promise<InvoiceDocument> {
  const totalPieces = invoiceData.details.reduce((sum, item) => {
    return sum + (parseFloat(item.quantity) || 0);
  }, 0);

  const totalCost = parseFloat(invoiceData.summary.invoiceTotal) || 0;

  const document:Partial<InvoiceDocument> = {
    invoice_number: invoiceData.invoiceNumber,
    invoice_date: invoiceData.invoiceDate,
    template_id: templateId,
    trading_partner_id: tradingPartnerId ,
    document_type: 'invoice',
    status: 'draft',
    total_pieces: totalPieces,
    total_cost: totalCost,
    data: invoiceData,
    user_id: userId
  };


  // Insert — MyRest may not return data on insert, so we insert then fetch
  const { error: insertError } = await getSupabaseClient()
    .from('documents')
    .insert(document);

  if (insertError) {
    console.error('createInvoiceDocument insert failed:', insertError);
    throw new Error(`Failed to create invoice document: ${insertError.message || JSON.stringify(insertError)}`);
  }

  // Fetch the just-created document
  const { data: rows, error: fetchError } = await getSupabaseClient()
    .from('documents')
    .select('*')
    .eq('document_type', 'invoice')
    .eq('invoice_number', document.invoice_number)
    .eq('user_id', document.user_id)
    .order('created_at', { ascending: false })
    .limit(1);

  if (fetchError || !rows || rows.length === 0) {
    console.error('createInvoiceDocument fetch-after-insert failed:', fetchError);
    throw new Error('Invoice document was inserted but could not be retrieved. Check database.');
  }

  const savedDoc = rows[0] as InvoiceDocument;
  return savedDoc;
}

export async function updateInvoiceDocument(
  id: string,
  invoiceData: InvoiceFormData,
  status: DocumentStatus
): Promise<InvoiceDocument> {
  const totalPieces = invoiceData.details.reduce((sum, item) => {
    return sum + (parseFloat(item.quantity) || 0);
  }, 0);

  const totalCost = parseFloat(invoiceData.summary.invoiceTotal) || 0;

  const updates: Record<string, unknown> = {
    invoice_number: invoiceData.invoiceNumber,
    invoice_date: invoiceData.invoiceDate,
    total_pieces: totalPieces,
    total_cost: totalCost,
    data: invoiceData,
  };

  if (status) {
    updates.status = status;
  }

  // trading_partner_id and template_id are set at creation time — never changed on update
  const { data, error } = await getSupabaseClient()
    .from('documents')
    .update(updates)
    .eq('id', id)
    .eq('document_type', 'invoice')
    .select()
    .single();

  if (error) {
    console.error('updateInvoiceDocument failed:', error);
    throw new Error(`Failed to update invoice document ${id}: ${error.message || JSON.stringify(error)}`);
  }

  const savedDoc = data as InvoiceDocument;
  return savedDoc;
}

export async function deleteInvoiceDocument(id: string): Promise<void> {
  return deleteDocument(id);
}

export async function duplicateInvoiceDocument(id: string, userId: string): Promise<InvoiceDocument> {
  const original = await fetchInvoiceDocumentById(id) as InvoiceDocument;
  if (!original) {
    throw new Error('Invoice document not found');
  }

  const newData = {
    ...original.data,
    invoiceNumber: `${original.data.invoiceNumber}-COPY`,
    status: undefined, // reset to draft
  };

  return createInvoiceDocument(newData, userId, original.template_id, original.trading_partner_id);
}


// ==========================================
// 5. COMPATIBILITY ALIASES
// ==========================================

export const fetchDocumentById = fetchASNDocumentById;
export const createDocument = createASNDocument;
export const updateDocument = updateASNDocument;
